import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #revealedCount = 0;

  #setupLoadMore() {
    if (this.dataset.loadMoreEnabled !== 'true') return;
    const button = this.querySelector('.section-resource-list__content > .button');
    const list = this.querySelector('.resource-list--grid');
    if (!(button instanceof HTMLAnchorElement) || !(list instanceof HTMLElement)) return;
    const items = Array.from(list.children).filter((item) => item instanceof HTMLElement && item.classList.contains('resource-list__item'));
    const batch = Math.max(1, Number(this.dataset.loadMoreBatch) || 20);
    this.#revealedCount = Math.max(batch, this.#revealedCount);
    items.forEach((item, index) => { item.hidden = index >= this.#revealedCount; });
    button.dataset.luxmoLoadMore = 'true';
    button.setAttribute('role', 'button');
    button.setAttribute('aria-controls', list.id);
    button.setAttribute('aria-expanded', String(this.#revealedCount > batch));
    button.hidden = this.#revealedCount >= items.length;
  }

  #onReadMore = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-luxmo-load-more]');
    if (!(button instanceof HTMLAnchorElement) || !this.contains(button)) return;
    event.preventDefault();
    const batch = Math.max(1, Number(this.dataset.loadMoreBatch) || 20);
    const hiddenItems = Array.from(this.querySelectorAll('.resource-list--grid > .resource-list__item[hidden]'));
    const firstNewLink = hiddenItems[0]?.querySelector('a');
    this.#revealedCount += Math.min(batch, hiddenItems.length);
    this.#setupLoadMore();
    if (firstNewLink instanceof HTMLElement) firstNewLink.focus({ preventScroll: true });
    const status = this.querySelector('[data-load-more-status]');
    if (status) status.textContent = String(this.#revealedCount);
  };

  #onReadMoreKey = (event) => {
    if (event.key !== ' ' || !(event.target instanceof HTMLAnchorElement) || !event.target.hasAttribute('data-luxmo-load-more')) return;
    event.preventDefault();
    event.target.click();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#setupLoadMore();
    this.addEventListener('click', this.#onReadMore);
    this.addEventListener('keydown', this.#onReadMoreKey);
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.removeEventListener('click', this.#onReadMore);
    this.removeEventListener('keydown', this.#onReadMoreKey);
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      this.#setupLoadMore();
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          return Promise.resolve(morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true }))
            .then(() => this.#setupLoadMore());
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    if (this.dataset.loadMoreEnabled === 'true' && this.querySelector('.luxmo-rec-card')) {
      this.#setupLoadMore();
    } else {
      this.classList.add('hidden');
    }
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

